import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BarChart3,
  Building2,
  CalendarDays,
  CircleAlert,
  ClipboardList,
  Database,
  ExternalLink,
  Flag,
  GraduationCap,
  Image,
  MapPin,
  MessageSquare,
  Package,
  PieChart,
  Settings,
  TrendingUp,
  Type,
  UserRoundCheck,
  Users,
} from "lucide-react";

import MonthwiseRegistrationsCard from "./graphs/MonthwiseRegistrationsCard";
import CountrywiseRegistrationsCard2 from "./graphs/CountryWiseRegistrationsCard2";
import IndustrySectorwiseCard from "./graphs/IndustrySectorwiseCard";
import EventDiscoryRegCharts from "./graphs/EventDiscoryRegCharts";
import useApiEp from "../hooks/useApiEp";
import { UserContext } from "../context/UserProvider";
import { USER_ROLES } from "../utils/constants";
import { isAdminRestrictedPath } from "../utils/accessControl";

const COLORS = {
  ink: "#172033",
  muted: "#6d7b91",
  border: "#e5eaf2",
  blue: "#2f66ee",
  green: "#1bad5b",
  purple: "#7c3aed",
  cyan: "#0d8dcc",
  orange: "#f97316",
};

const panelSx = {
  height: "100%",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 2.5,
  boxShadow: "0 6px 18px rgba(20, 36, 66, 0.055)",
  transition: "transform 180ms ease, box-shadow 180ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 11px 26px rgba(20, 36, 66, 0.09)",
  },
};

const formatCount = (value) =>
  typeof value === "number" ? value.toLocaleString() : null;

const useRoleCount = (role) =>
  useApiEp(`/admin/get-users-counter-by-role?role=${encodeURIComponent(role)}`);

const IconBadge = ({ children, color, background }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      color,
      bgcolor: background,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const CountCard = ({
  title,
  count,
  loading,
  icon,
  color,
  background,
  link,
  action,
}) => (
  <Card sx={panelSx}>
    <CardContent
      sx={{
        p: { xs: 1.75, sm: 2 },
        "&:last-child": { pb: { xs: 1.75, sm: 2 } },
        minHeight: 210,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <IconBadge color={color} background={background}>
        {icon}
      </IconBadge>
      <Typography
        sx={{ color: COLORS.muted, fontSize: 14, fontWeight: 600, mt: 1.5 }}
      >
        {title}
      </Typography>
      {loading ? (
        <Skeleton width="55%" height={46} sx={{ my: 0.35 }} />
      ) : (
        <Typography
          sx={{
            color: COLORS.ink,
            fontSize: { xs: 30, lg: 34 },
            lineHeight: 1.2,
            fontWeight: 800,
            my: 0.5,
          }}
        >
          {formatCount(count) ?? "—"}
        </Typography>
      )}
      <Button
        component={Link}
        to={link}
        endIcon={<ArrowRight size={16} />}
        sx={{
          mt: "auto",
          justifyContent: "space-between",
          px: 1.4,
          py: 0.85,
          color,
          bgcolor: background,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          "&:hover": { bgcolor: background, filter: "brightness(0.98)" },
        }}
      >
        {action}
      </Button>
    </CardContent>
  </Card>
);

const ModuleCard = ({ title, description, icon, color, background, link }) => (
  <Card sx={panelSx}>
    <CardContent
      sx={{
        p: 2,
        "&:last-child": { pb: 2 },
        minHeight: 205,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <IconBadge color={color} background={background}>
        {icon}
      </IconBadge>
      <Typography
        sx={{ color: COLORS.ink, fontSize: 16, fontWeight: 750, mt: 1.5 }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: COLORS.muted, fontSize: 13.5, lineHeight: 1.4, mt: 0.3 }}
      >
        {description}
      </Typography>
      <Button
        component={Link}
        to={link}
        endIcon={<ArrowRight size={16} />}
        sx={{
          mt: "auto",
          justifyContent: "space-between",
          px: 1.4,
          py: 0.85,
          color,
          bgcolor: background,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          "&:hover": { bgcolor: background, filter: "brightness(0.98)" },
        }}
      >
        Open module
      </Button>
    </CardContent>
  </Card>
);

const MiniMetric = ({ title, value, loading, icon, color, background }) => (
  <Card sx={panelSx}>
    <CardContent
      sx={{
        p: 2,
        "&:last-child": { pb: 2 },
        minHeight: 160,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <IconBadge color={color} background={background}>
          {icon}
        </IconBadge>
        <TrendingUp size={34} color={color} strokeWidth={2} />
      </Box>
      <Box>
        {loading ? (
          <Skeleton width="45%" height={38} />
        ) : (
          <Typography
            sx={{
              color: COLORS.ink,
              fontSize: 26,
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            {formatCount(value) ?? "—"}
          </Typography>
        )}
        <Typography
          sx={{ color: COLORS.muted, fontSize: 13.5, fontWeight: 500, mt: 0.2 }}
        >
          {title}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isSuperAdmin = user?.role === USER_ROLES.SUPER;

  const visitors = useRoleCount(USER_ROLES.VISITOR);
  const exhibitors = useRoleCount(USER_ROLES.EXHIBITOR);
  const invitees = useRoleCount(USER_ROLES.SPECIAL_INVITEE);
  const students = useRoleCount(USER_ROLES.STUDENT);
  const team = useRoleCount(USER_ROLES.TEAM);

  const { data: eventSettings, isSuccess: eventSettingsLoaded } = useApiEp(
    `/admin/get-event-settings`
  );
  const { data: countrywiseData, isLoading: countriesLoading } = useApiEp(
    `/user/get-countrywise-registrations`
  );
  const { data: products, isLoading: productsLoading } = useApiEp(
    `/admin/count-products`
  );
  const { data: oesSubmits, isLoading: oesLoading } = useApiEp(
    `/admin/count-oes-submits`
  );
  const { data: delegates, isLoading: delegatesLoading } = useApiEp(
    `/admin/count-delegates`,
    !isAdmin
  );
  const { data: pendingDelegates, isLoading: pendingLoading } = useApiEp(
    `/admin/count-pending-delegates`,
    !isAdmin
  );

  useEffect(() => {
    if (user?.role === USER_ROLES.TD_ADMIN) navigate(`/manage-delegates`);
  }, [navigate, user]);

  const countries = countrywiseData?.registrations?.filter(
    (item) => item.country
  )?.length;
  const displayName = user?.name || user?.username || "Admin";
  const eventName = eventSettings?.eventName || "your event";
  const requiredEventSettings = [
    {
      key: "eventName",
      label: "Event name",
      icon: <Type size={19} />,
    },
    {
      key: "eventLogo",
      label: "Event logo",
      icon: <Image size={19} />,
    },
    {
      key: "eventStartDate",
      label: "Event start date",
      icon: <CalendarDays size={19} />,
    },
    {
      key: "eventEndDate",
      label: "Event end date",
      icon: <CalendarDays size={19} />,
    },
  ];
  const missingEventSettings = requiredEventSettings.filter(({ key }) => {
    const value = eventSettings?.[key];
    return typeof value === "string" ? !value.trim() : !value;
  });
  const showEventSetupDialog =
    !isAdmin && eventSettingsLoaded && missingEventSettings.length > 0;

  const counts = [
    {
      title: "Exhibitors",
      count: exhibitors.data?.count,
      loading: exhibitors.isLoading,
      icon: <Building2 size={24} />,
      color: COLORS.blue,
      background: "#edf3ff",
      link: "/exhibitors-list",
      action: "View exhibitors",
    },
    {
      title: "Visitors",
      count: visitors.data?.count,
      loading: visitors.isLoading,
      icon: <Users size={25} />,
      color: COLORS.green,
      background: "#ecfbf2",
      link: "/visitors-list",
      action: "View visitors",
    },
    {
      title: "Special Invitees",
      count: invitees.data?.count,
      loading: invitees.isLoading,
      icon: <Award size={25} />,
      color: COLORS.purple,
      background: "#f4edff",
      link: "/special-invitees",
      action: "View invitees",
    },
    {
      title: "Students",
      count: students.data?.count,
      loading: students.isLoading,
      icon: <GraduationCap size={26} />,
      color: COLORS.cyan,
      background: "#eaf7fd",
      link: "/students-list",
      action: "View students",
    },
  ];

  const modules = [
    {
      title: "Registration Forms",
      description: "Registration links & form setup",
      icon: <MessageSquare size={24} />,
      color: COLORS.blue,
      background: "#edf3ff",
      link: "/form-links",
    },
    {
      title: "Registration Analytics",
      description: "Current vs previous registrations",
      icon: <BarChart3 size={24} />,
      color: COLORS.green,
      background: "#ecfbf2",
      link: "/registration-analytics",
    },
    {
      title: "Catalogue",
      description: "Products & exhibitor forms",
      icon: <PieChart size={24} />,
      color: COLORS.purple,
      background: "#f4edff",
      link: "/catalogue",
    },
    {
      title: "Onsite Module",
      description: "Passes & onsite actions",
      icon: <MapPin size={24} />,
      color: COLORS.orange,
      background: "#fff4e9",
      link: "/onsite-links",
    },
    {
      title: "Database",
      description: "Participant data & exports",
      icon: <Database size={24} />,
      color: "#4f46e5",
      background: "#eef0ff",
      link: "/export-data",
    },
  ].filter((item) => !isAdmin || !isAdminRestrictedPath(item.link));

  return (
    <>
      <Dialog
        open={showEventSetupDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="event-setup-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 24px 70px rgba(23, 32, 51, 0.22)",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            pt: 3,
            pb: 2.25,
            background:
              "linear-gradient(135deg, #edf3ff 0%, #f7f9ff 55%, #f4edff 100%)",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "grid",
              placeItems: "center",
              color: COLORS.blue,
              bgcolor: "#fff",
              borderRadius: "50%",
              boxShadow: "0 8px 20px rgba(47, 102, 238, 0.16)",
              mb: 1.5,
            }}
          >
            <CircleAlert size={25} />
          </Box>
          <DialogTitle
            id="event-setup-dialog-title"
            sx={{ p: 0, color: COLORS.ink, fontSize: 23, fontWeight: 800 }}
          >
            Complete your event setup
          </DialogTitle>
          <Typography sx={{ color: COLORS.muted, fontSize: 14.5, mt: 0.6 }}>
            Event-specific settings are not complete. Add the missing details
            below before continuing.
          </Typography>
        </Box>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.25 }}>
          <List disablePadding>
            {missingEventSettings.map((setting) => (
              <ListItem
                key={setting.key}
                sx={{
                  px: 1.5,
                  py: 1,
                  mb: 1,
                  border: "1px solid #e5eaf2",
                  borderRadius: 2,
                  bgcolor: "#f8faff",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: COLORS.blue }}>
                  {setting.icon}
                </ListItemIcon>
                <ListItemText
                  primary={setting.label}
                  secondary="Required"
                  primaryTypographyProps={{
                    color: COLORS.ink,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate("/settings")}
            endIcon={<ArrowRight size={18} />}
            sx={{
              py: 1.15,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 750,
              boxShadow: "none",
            }}
          >
            OK, go to settings
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          minHeight: "100%",
          bgcolor: "#f7f9fc",
          mx: { xs: -1, sm: -2 },
          mt: -1,
          px: { xs: 2, sm: 3 },
          py: 2.5,
        }}
      >
        <Box sx={{ maxWidth: 1680, mx: "auto" }}>
          <Grid container spacing={1.75} alignItems="stretch">
            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  ...panelSx,
                  minHeight: 210,
                  color: "white",
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, #111a2d 0%, #17284a 45%, #2458d7 100%)",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 240,
                    height: 240,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.06)",
                    right: -75,
                    top: -110,
                  }}
                />
                <CardContent
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    "&:last-child": { pb: { xs: 2, md: 2.5 } },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <Typography
                    sx={{ fontSize: { xs: 21, md: 23 }, fontWeight: 800 }}
                  >
                    Welcome back, {displayName}! 👋
                  </Typography>
                  <Typography sx={{ opacity: 0.75, fontSize: 14, mt: 0.4 }}>
                    Manage {eventName} from one place
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.25,
                      mt: "auto",
                      pt: 3.5,
                    }}
                  >
                    {eventSettings?.userDashLink && (
                      <Button
                        component="a"
                        href={eventSettings.userDashLink}
                        target="_blank"
                        rel="noreferrer"
                        variant="outlined"
                        endIcon={<ExternalLink size={17} />}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.45)",
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: "white",
                            bgcolor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        User login
                      </Button>
                    )}
                    {!isAdmin && (
                      <Button
                        component={Link}
                        to="/settings"
                        variant="outlined"
                        startIcon={<Settings size={18} />}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.45)",
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          "&:hover": {
                            borderColor: "white",
                            bgcolor: "rgba(255,255,255,0.08)",
                          },
                        }}
                      >
                        Event settings
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {counts.map((item) => (
              <Grid item xs={12} sm={6} lg={2} key={item.title}>
                <CountCard {...item} />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: `repeat(${isAdmin ? 3 : 5}, minmax(0, 1fr))`,
              },
              gap: 1.75,
              mt: 1.75,
            }}
          >
            {modules.map((item) => (
              <Box key={item.title} sx={{ minWidth: 0 }}>
                <ModuleCard {...item} />
              </Box>
            ))}
          </Box>

          <Grid container spacing={1.75} sx={{ mt: 0 }} alignItems="stretch">
            {!isAdmin && (
              <Grid item xs={12} lg={4}>
                <Card sx={panelSx}>
                <CardContent
                  sx={{
                    p: 2,
                    "&:last-child": { pb: 2 },
                    minHeight: 160,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <IconBadge color={COLORS.blue} background="#edf3ff">
                      <UserRoundCheck size={24} />
                    </IconBadge>
                    <Typography
                      sx={{ color: COLORS.ink, fontSize: 17, fontWeight: 800 }}
                    >
                      Trade Delegates
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.4,
                        bgcolor: "#f7f9fc",
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.muted,
                          fontSize: 12,
                          fontWeight: 650,
                        }}
                      >
                        Total approved
                      </Typography>
                      <Typography
                        sx={{
                          color: COLORS.blue,
                          fontSize: 22,
                          fontWeight: 800,
                        }}
                      >
                        {delegatesLoading
                          ? "…"
                          : formatCount(delegates?.count) ?? "—"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.4,
                        bgcolor: "#fff8eb",
                        border: "1px solid #fedda5",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        sx={{ color: "#b86608", fontSize: 12, fontWeight: 650 }}
                      >
                        Needs approval
                      </Typography>
                      <Typography
                        sx={{ color: "#d97706", fontSize: 22, fontWeight: 800 }}
                      >
                        {pendingLoading
                          ? "…"
                          : formatCount(pendingDelegates?.count) ?? "—"}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    component={Link}
                    to="/manage-delegates"
                    variant="contained"
                    endIcon={<ArrowRight size={18} />}
                    sx={{
                      mt: 1.35,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "none",
                    }}
                  >
                    {isSuperAdmin
                      ? "Review registrations"
                      : "Manage delegates"}
                  </Button>
                </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} sm={6} lg={isAdmin ? 3 : 2}>
              <MiniMetric
                title="Countries"
                value={countries}
                loading={countriesLoading}
                icon={<Flag size={23} />}
                color={COLORS.purple}
                background="#f4edff"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={isAdmin ? 3 : 2}>
              <MiniMetric
                title="Product uploads"
                value={products?.count}
                loading={productsLoading}
                icon={<Package size={23} />}
                color={COLORS.blue}
                background="#edf3ff"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={isAdmin ? 3 : 2}>
              <MiniMetric
                title="OES submits"
                value={oesSubmits?.count}
                loading={oesLoading}
                icon={<ClipboardList size={23} />}
                color="#10a979"
                background="#e9fbf4"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={isAdmin ? 3 : 2}>
              <MiniMetric
                title="Team members"
                value={team.data?.count}
                loading={team.isLoading}
                icon={<Users size={23} />}
                color="#ed8b00"
                background="#fff6e5"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Typography
              sx={{ color: COLORS.ink, fontSize: 21, fontWeight: 800, mb: 1.5 }}
            >
              Registration insights
            </Typography>
            <Grid container spacing={1.75}>
              <Grid item xs={12} md={6}>
                <MonthwiseRegistrationsCard />
              </Grid>
              <Grid item xs={12} md={6}>
                <CountrywiseRegistrationsCard2 />
              </Grid>
              <Grid item xs={12} md={6}>
                <IndustrySectorwiseCard />
              </Grid>
              <Grid item xs={12} md={6}>
                <EventDiscoryRegCharts />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
